import {
    CountrySpecificConfig,
    DocumentType,
    LaunchDarklyProject,
    PushNotificationType,
} from '@superbet-group/web.lib.config';

import BonusProvider from '@core/models/bonus/BonusProvider';
import { Environment } from '@src/config/enums';
import { appLocale } from '@src/config/locale';
import { serviceProps } from '@src/config/playerAccountManagement/Comtrade';
import { HistoryType } from '@store/modules/ui/user/history/enums';
import { Country } from '@superbet-group/web.types';

const isProd = process.env.NODE_ENV === 'production';

const config: CountrySpecificConfig = {
    country: Country.Pl,
    waitForRouteConfig: false,
    isBingoEnabled: false,
    checkSessionBeforeGameLaunch: false,
    services: {
        cashout: {
            isAvailable: true,
        },
    },
    header: {
        navbarItems: [
            'promotionHub',
            'sport',
            'live',
            'promotions',
            'cardGames',
            'virtual',
            'social',
            'results',
            'news',
        ],
        items: [
            'sport',
            'live',
            'games',
            'promotions',
            'news',
            'results',
            'shops',
        ],
    },
    auth: {
        usernameValidChars: '[A-Za-z0-9@$!%*#?&ăâîşţąćęłńóśźż]+',
        nameValidChars: "[A-Za-zăâîşţąćęłńóśźżĄćĆęĘłŁńŃóÓśŚźŹżŻÀ-ÖØ-öø-ÿ0-9\\-'\\s]+",
        polishNationality: 'Polska',
        countryId: 174,
        portalDesktopId: 'Desktop',
        portalMobileId: 'Mobile',
        registerUrl: 'registrationFirstStep',
        phonePrefix: '+48',
        ibanPrefix: 'PL',
        emailMaxLength: 50,
        username: {
            requirements: {
                minLength: 6,
                maxLength: 20,
            },
        },
        password: {
            requirements: {
                minLength: 8,
                maxLength: 30,
                hasLowercase: true,
                hasUppercase: true,
                hasDigit: true,
            },
            isDistinctFromUsername: false,
            isDistinctFromEmail: false,
        },
    },
    user: {
        activateAccountRestRoute: 'activateWithLogin',
        idCardVerification: {
            period: 90, // in days. if user doesn't upload id card account will be disabled,
            isCompleteRegistrationNeededForKyc: true,
            maxRequiredDocumentImages: 2,
            isRemainingTimeVisible: true,
        },
        withdrawal: {
            detailsBankTransferWithdrawalUrl: 'https://superbet.pl/wiki/metody-platnosci-online',
            isTaxable: false,
            isNewLabelVisible: {},
            hasFees: false,
            isDepositRequired: true,
            idCardVerificationCheck: {
                isRequired: true,
                isRemainingTimeVisible: true,
            },
            walletBreakdown: {
                isEnabled: false,
                isShown: false,
            },
        },
        deposit: {
            hasEligibility: false,
            idCardVerificationCheck: {
                isRequired: false,
                isRemainingTimeVisible: false,
            },
            isTaxable: false,
            isNewLabelVisible: {},
            bankTransfer: {
                iban: 'PL30 1600 1462 1087 3147 3000 0001',
                bank: 'BNP Paribas',
                currency: 'PLN',
                accountName: 'Superbet Zakłady Bukmacherskie Sp. z o.o.',
                swift: 'PPABPLPKXXX',
                city: 'Katowice',
            },
        },
        history: {
            availableTypes: {
                [HistoryType.transactions]: true,
                [HistoryType.bets]: true,
                [HistoryType.liveDealer]: true,
            },
        },
        tempAccountMaxWin: 2279.99,
        bonus: {
            faqUrlActive: isProd ? 'https://support.superbet.pl/hc/pl-pl/articles/360017198639-Gdzie-mog%C4%99-zobaczy%C4%87-aktywne-bonusy-na-moim-koncie-' : 'https://superbetpl1744015043.zendesk.com/hc/pl-pl/articles/360017198639-Gdzie-mog%C4%99-zobaczy%C4%87-aktywne-bonusy-na-moim-koncie-',
            faqUrlPending: isProd ? 'https://support.superbet.pl/hc/pl-pl/articles/360017150820-Jak-sprawdzi%C4%87-jakie-bonusy-mam-oczekuj%C4%85ce-i-jak-je-obr%C3%B3ci%C4%87-' : 'https://superbetpl1744015043.zendesk.com/hc/pl-pl/articles/360017150820-Jak-sprawdzi%C4%87-jakie-bonusy-mam-oczekuj%C4%85ce-i-jak-je-obr%C3%B3ci%C4%87-',
            isWelcomeBonusShown: true,
            bonusProvider: BonusProvider.Comtrade,
            isLiveDealerBonusVisible: true,
            isWelcomeBonusIntroModalVisible: false,
            availableDetailFields: {
                bonusAmount: true,
                redeemedAmount: true,
                wageringRequirements: true,
                wageredAmount: true,
                amountLeftToWager: false,
                spinCasinoDetail: false,
                acceptanceDate: true,
                activationDate: true,
                termsAndConditionsUrl: true,
                expirationDate: false,
                amountGained: false,
            },
        },
        settings: {
            phoneDigitsNumber: 9,
            isPhoneWithPrefixEnabled: false,
            defaultCountryPrefix: '+48',
        },
        isUpdateCustomParamsEnabled: true,
        isLicencePreferencesEnabled: false,
    },
    multiStepIdVerification: {
        documentSettings: {
            [DocumentType.Id]: {
                isEnabled: true,
                isFrontRequired: true,
                isBackRequired: true,
                isSelfieRequired: false,
            },
            [DocumentType.Passport]: {
                isEnabled: true,
                isFrontRequired: true,
                isBackRequired: false,
                isSelfieRequired: false,
            },
            [DocumentType.AdditionalDocument]: {
                isEnabled: true,
                isFrontRequired: true,
                isBackRequired: false,
                isSelfieRequired: false,
            },
        },
    },
    sport: {
        isStreamAvailable: true,
        excludedStreamTournaments: [],
        v5Config: {
            language: appLocale.languageCode,
            applicationVariant: 'plsuperbetsport',
        },
        areTaxDetailsVisible: true,
        timeMarginForEventStart: 1,
    },
    utils: {
        bookieShopStartTime: 9,
        dateOfBirthFormat: 'yyyy-MM-dd',
    },
    footer: {
        // not sure the links are these or staging isn't up to date
        whatsAppNumber: '',
        nonStopPhoneNumber: '',
        email: 'customer.support@superbet.pl',
        faqPage: isProd ? 'https://support.superbet.pl/hc/pl-pl' : 'https://superbetpl1744015043.zendesk.com/hc/pl-pl',
        offers: {
            daily: '/pdf/Superbet - Oferta dzisiejsza.pdf?var=',
            weekly: '/pdf/Superbet - Oferta tygodniowa.pdf?var=',
        },
        social: {
            twitter: 'https://twitter.com/SuperbetPL',
            facebook: 'https://www.facebook.com/superbet.polska/',
            youtube: 'https://www.youtube.com/channel/UCbEoeT66zcRudWXXH9JzsYQ',
            instagram: 'https://www.instagram.com/superbetpl/',
            tiktok: '',
        },
    },
    defaultLocation: {
        latitude: 52.2297,
        longitude: 21.0122,
    },
    getSessionDuration: 2 * 60 * 1000, // 2 minute
    checkLoginSessionDuration: 60 * 1000, // 1 minute
    isCasinoEnabled: false,
    showXJackpotOnPromoPage: false,
    news: {
        pageSize: 50,
        host: 'https://superbet-sportal-production.freetls.fastly.net/',
        project: 'superbet.pl',
    },
    corona: {
        areShopsDisabled: false,
    },
    supportEmail: 'customer.support@superbet.pl',
    virtualTicketsTranslations: {
        over: 'powyżej',
        under: 'poniżej',
    },
    betSlip: {
        allowSubmitWithMaxWin: false,
        showPayoutShoppingList: true,
        validationsConfig: {
            isEnabledWinLowerThanStake: false,
        },
    },
    marketFilters: {
        isEnabled: true,
    },
    maintenance: {
        defaultMaintenanceUrl: 'https://maintenance.web.superbet.pl/',
    },
    multiStepTaxForSystemTickets: true,
    isPrintHistoryEnabled: false,
    launchDarkly: {
        clientIds: {
            [LaunchDarklyProject.DefaultProject]: '',
            [LaunchDarklyProject.ServiceStatus]: '',
        },
        userKeyVariable: serviceProps.launchDarklyUserKeyVariable,
    },
    social: {
        country: Country.Pl,
        env: 'stage',
    },
    pushNotificationType: PushNotificationType.NotificationSocketService,
    useSessionActivity: true,
    moreInformationLink: isProd ? 'https://support.superbet.pl/hc/pl-pl/articles/360011893360-Metody-p%C5%82atno%C5%9Bci' : 'https://superbetpl1744015043.zendesk.com/hc/pl-pl/articles/360011893360-Metody-p%C5%82atno%C5%9Bci',
    isSessionDurationDisplayed: true,
    inbox: {
        availableDeepLinks: ['plsuperbetsport', 'plsuperbetgames'],
    },
};

const defaultProjectLDClientIds: Record<Environment, string> = {
    Dev: '60f55251803ad725c4998b50',
    Qa: '60f552a72ad8b92590920b9a',
    Stage: '60f552f8fc94ed24a3e40b07',
    Prod: '60f5666e379d910d480d6a8a',
};

const serviceStatusLDClientIds: Record<Environment, string> = {
    Dev: '614c5127d67470260ab05c4e',
    Qa: '614c5127d67470260ab05c4e',
    Stage: '614c5127d67470260ab05c4e',
    Prod: '614c50cdbbbf43269daae893',
};

switch (window.location.hostname) {
    case 'www.superbet.pl':
    case 'superbet.pl':
    case 'be.superbet.pl':
    case 'local.prod.superbet.pl':
    case 'www.silent.superbet.pl':
    case 'silent.superbet.pl':
    case 'stage.on.superbet.pl':
    case 'stage-be.on.superbet.pl':
        config.launchDarkly.clientIds = {
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Prod,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Prod,
        };
        config.social.env = 'prod';
        break;
    case 'stage.superbet.pl':
    case 'stage-be.superbet.pl':
    case 'playground.superbet.pl':
    case 'www.stage.superbet.pl':
    case 'local.stage.superbet.pl':
    case 'local.stage-be.superbet.pl':
        config.launchDarkly.clientIds = {
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Stage,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Stage,
        };
        break;
    case 'qa.superbet.pl':
    case 'qa-be.superbet.pl':
    case 'www.qa.superbet.pl':
    case 'local.qa.superbet.pl':
    case 'local.qa-be.superbet.pl':
        config.launchDarkly.clientIds = {
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Qa,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Qa,
        };
        break;
    case 'dev.superbet.pl':
    case 'dev-be.superbet.pl':
    case 'local.dev.superbet.pl':
    case 'local.dev-be.superbet.pl':
    case 'local.superbet.pl':
        config.launchDarkly.clientIds = {
            [LaunchDarklyProject.DefaultProject]: defaultProjectLDClientIds.Dev,
            [LaunchDarklyProject.ServiceStatus]: serviceStatusLDClientIds.Dev,
        };
        break;
    default:
        break;
}

export default config;
